import type { InvitationEntity, MembershipEntity, PermissionEntity, PermissionTemplateEntity, ProfileContactMatchEntity, ProfileSearchEntity, RoleEntity } from '../../domain/entities/MembershipEntity.js';
import type { IMembershipRepository, CreateUserPayload } from '../../domain/repositories/IMembershipRepository.js';
import { MembershipRemoteDataSource } from '../datasources/MembershipRemoteDataSource.js';

export class MembershipRepository implements IMembershipRepository {
  constructor(private readonly ds: MembershipRemoteDataSource) {}

  async listMemberships(orgId: string, projectId?: string, roleId?: string, limit?: number, offset?: number, profileId?: string): Promise<{ data: MembershipEntity[]; total: number }> {
    const { data: rows, total } = await this.ds.listMemberships(orgId, projectId, roleId, limit, offset, profileId);
    return {
      data: rows.map((r: any) => ({
        id: r.id, profileId: r.profile_id, orgId: r.org_id,
        projectId: r.project_id ?? null, roleId: r.role_id, createdAt: r.created_at,
        profileEmail: r.profile?.email, profileName: `${r.profile?.first_name ?? ''} ${r.profile?.last_name ?? ''}`.trim(),
        profileFirstName: r.profile?.first_name ?? '', profileLastName: r.profile?.last_name ?? '',
        profilePhone: r.profile?.phone ?? '',
        roleName: r.role?.name,
        metadatas: r.metadatas ?? null,
      })),
      total,
    };
  }

  async listProfileIdsByTeam(orgId: string, projectId: string, team: string): Promise<string[]> {
    return this.ds.listProfileIdsByTeam(orgId, projectId, team);
  }

  async searchMembers(input: { orgId: string; projectId?: string; search?: string; limit?: number }): Promise<MembershipEntity[]> {
    const rows = await this.ds.searchMembers(input.orgId, { projectId: input.projectId, search: input.search, limit: input.limit });
    return rows.map((r: any) => ({
      id: r.id, profileId: r.profile_id, orgId: r.org_id,
      projectId: r.project_id ?? null, roleId: r.role_id, createdAt: r.created_at,
      profileEmail: r.profile_data?.email, profileName: `${r.profile_data?.first_name ?? ''} ${r.profile_data?.last_name ?? ''}`.trim(),
      profileFirstName: r.profile_data?.first_name ?? '', profileLastName: r.profile_data?.last_name ?? '',
      profilePhone: r.profile_data?.phone ?? '',
      roleName: r.role_data?.name,
    }));
  }

  async searchProfiles(input: { search?: string; provinceCode?: string }): Promise<ProfileSearchEntity[]> {
    const rows = await this.ds.searchProfiles(input);
    return rows.map((r: any) => ({
      id: r.id, firstName: r.first_name ?? '', lastName: r.last_name ?? '',
      email: r.email ?? null, phone: r.phone ?? null, avatarUrl: r.avatar_url ?? null,
      provinceCode: r.province_code ?? null, birthDate: r.birth_date ?? null, gender: r.gender ?? null,
      heightCm: r.height_cm ?? null, weightKg: r.weight_kg ?? null, workExperiences: r.work_experiences ?? [],
    }));
  }

  async searchProfilesByContactExact(input: { query: string; orgId?: string; projectId?: string }): Promise<ProfileContactMatchEntity[]> {
    const rows = await this.ds.searchProfilesByContactExact(input.query, input.orgId, input.projectId);
    return rows.map((r: any) => ({
      id: r.profile_id, firstName: r.first_name ?? '', lastName: r.last_name ?? '',
      email: r.email ?? null, phone: r.phone ?? null,
      matchedField: r.matched_field, inOrg: !!r.in_org, inProject: !!r.in_project,
    }));
  }

  async bulkAddMembers(input: { orgId: string; projectId: string | null; profileIds: string[]; roleId: string; metadatas?: Record<string, any> | null }): Promise<void> {
    await this.ds.bulkAddMembers(input.orgId, input.projectId, input.profileIds, input.roleId, input.metadatas);
  }

  async createUserWithMembership(input: CreateUserPayload): Promise<{ userId: string; email: string }> {
    return this.ds.createUserWithMembership(input);
  }

  async createUsersBulk(input: CreateUserPayload[]): Promise<Array<{ email: string; ok: boolean; userId?: string; error?: string }>> {
    return this.ds.createUsersBulk(input);
  }

  async removeMember(membershipId: string): Promise<void> {
    await this.ds.removeMember(membershipId);
  }

  async listInvitations(orgId: string, projectId?: string): Promise<InvitationEntity[]> {
    const rows = await this.ds.listInvitations(orgId, projectId);
    return rows.map((r: any) => ({
      id: r.id, orgId: r.org_id, projectId: r.project_id ?? null, sessionId: r.session_id ?? null, roleId: r.role_id,
      code: r.code, note: r.note ?? null, expiresAt: r.expires_at ?? null,
      maxUses: r.max_uses ?? null, usedCount: r.used_count ?? 0, isActive: r.is_active,
      createdAt: r.created_at, roleName: r.role?.name, projectName: r.project?.name ?? null,
    }));
  }

  async createInvitation(input: {
    orgId: string; roleId: string; projectId?: string | null; sessionId?: string | null;
    note?: string | null; expiresAt?: string | null; maxUses?: number | null; createdBy: string;
  }): Promise<InvitationEntity> {
    const data = await this.ds.createInvitation({
      org_id: input.orgId, role_id: input.roleId, project_id: input.projectId ?? null,
      session_id: input.sessionId ?? null,
      note: input.note ?? null, expires_at: input.expiresAt ?? null,
      max_uses: input.maxUses ?? null, created_by: input.createdBy,
    });
    if (!data) throw new Error('Invitation creation failed');
    return {
      id: data.id, orgId: data.org_id, projectId: data.project_id ?? null, sessionId: data.session_id ?? null, roleId: data.role_id,
      code: data.code, note: data.note ?? null, expiresAt: data.expires_at ?? null,
      maxUses: data.max_uses ?? null, usedCount: data.used_count ?? 0,
      isActive: data.is_active, createdAt: data.created_at, roleName: data.role?.name,
    };
  }

  async deactivateInvitation(id: string): Promise<void> {
    await this.ds.deactivateInvitation(id);
  }

  async updateMemberRole(membershipId: string, roleId: string): Promise<void> {
    await this.ds.updateMemberRole(membershipId, roleId);
  }

  async updateMemberMetadata(membershipId: string, metadatas: Record<string, any>): Promise<void> {
    await this.ds.updateMemberMetadata(membershipId, metadatas);
  }

  async listRoles(orgId: string): Promise<RoleEntity[]> {
    const rows = await this.ds.listRoles(orgId);
    return rows.map((r: any) => ({ id: r.id, orgId: r.org_id ?? null, name: r.name, description: r.description ?? null }));
  }

  async createRole(input: { orgId: string; name: string; description?: string | null }): Promise<RoleEntity> {
    const r = await this.ds.createRole(input.orgId, input.name, input.description);
    if (!r) throw new Error('Role creation failed');
    return { id: r.id, orgId: r.org_id ?? null, name: r.name, description: r.description ?? null };
  }

  async updateRole(input: { id: string; name: string; description?: string | null }): Promise<void> {
    await this.ds.updateRole(input.id, input.name, input.description);
  }

  async deleteRole(id: string): Promise<void> {
    await this.ds.deleteRole(id);
  }

  async listPermissions(orgId: string): Promise<PermissionEntity[]> {
    const rows = await this.ds.listPermissions(orgId);
    return rows.map((r: any) => ({ id: r.id, orgId: r.org_id ?? null, resource: r.resource, action: r.action }));
  }

  async createPermission(input: { orgId: string; resource: string; action: string }): Promise<PermissionEntity> {
    const r = await this.ds.createPermission(input.orgId, input.resource, input.action);
    if (!r) throw new Error('Permission creation failed');
    return { id: r.id, orgId: r.org_id ?? null, resource: r.resource, action: r.action };
  }

  async updatePermission(input: { id: string; resource: string; action: string }): Promise<void> {
    await this.ds.updatePermission(input.id, input.resource, input.action);
  }

  async deletePermission(id: string): Promise<void> {
    await this.ds.deletePermission(id);
  }

  async listPermissionsForRoles(roleIds: string[]): Promise<Record<string, PermissionEntity[]>> {
    const raw = await this.ds.listPermissionsForRoles(roleIds);
    const result: Record<string, PermissionEntity[]> = {};
    for (const [roleId, perms] of Object.entries(raw)) {
      result[roleId] = perms.map((r: any) => ({ id: r.id, orgId: r.org_id ?? null, resource: r.resource, action: r.action }));
    }
    return result;
  }

  async listPermissionsOfRole(roleId: string): Promise<PermissionEntity[]> {
    const rows = await this.ds.listPermissionsOfRole(roleId);
    return rows.map((r: any) => ({ id: r.id, orgId: r.org_id ?? null, resource: r.resource, action: r.action }));
  }

  async assignPermissionToRole(input: { roleId: string; permissionId: string }): Promise<void> {
    await this.ds.assignPermissionToRole(input.roleId, input.permissionId);
  }

  async removePermissionFromRole(input: { roleId: string; permissionId: string }): Promise<void> {
    await this.ds.removePermissionFromRole(input.roleId, input.permissionId);
  }

  async getInvitationByCode(code: string): Promise<InvitationEntity | null> {
    const r = await this.ds.getInvitationByCode(code);
    if (!r) return null;
    return {
      id: r.id, orgId: r.org_id, projectId: r.project_id ?? null, sessionId: r.session_id ?? null, roleId: r.role_id,
      code: r.code, note: r.note ?? null, expiresAt: r.expires_at ?? null,
      maxUses: r.max_uses ?? null, usedCount: r.used_count ?? 0, isActive: r.is_active,
      createdAt: r.created_at, roleName: r.role?.name,
    };
  }

  async redeemInvitation(code: string, profileId: string): Promise<void> {
    await this.ds.redeemInvitation(code, profileId);
  }

  async checkExistingEmails(emails: string[]): Promise<string[]> {
    return this.ds.checkExistingEmails(emails);
  }

  async exportPermissionTemplate(orgId: string): Promise<PermissionTemplateEntity> {
    return this.ds.exportPermissionTemplate(orgId);
  }

  async importPermissionTemplate(orgId: string, template: PermissionTemplateEntity): Promise<void> {
    return this.ds.importPermissionTemplate(orgId, template);
  }
}
